import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  getTableElement,
  getTableObserverFromTableElement,
} from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import {
  $addUpdateTag,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  LexicalCommand,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecordEditAction } from 'src/UI/app-container/sidebar/useRecordEditAction';
import { buildEditActionTelemetry } from 'src/UI/app-container/sidebar/editActionTelemetry';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useInlineDocumentEdit } from 'src/document-editor/hooks/useInlineDocumentEdit';
import type { DocumentEditOwner } from 'src/document-editor/documentOwner';
import { createTransformers } from '../config/transformers';
import { $createSuggestionNode } from '../nodes/SuggestionNode';
import { TableSelectionInfo } from '../types';
import {
  $getEditorValue,
  $getTableCellMarkdown,
  $getTableCellsMarkdown,
  $getTableSelectionMarkdown,
  $insertReplacementMarkers,
  $isPartialColumnSelection,
  $removeReplacementMarkers,
  $setEditorValue,
  hoistReplacementTags,
} from '../utils';
import { captureScrollPosition } from '../utils/captureScrollPosition';

type PendingRequest = {
  markdown: string;
  promptText: string;
  tableSelectionInfo?: TableSelectionInfo;
};

type SiestaRequestPayload = {
  promptText: string;
  toolbarTopPercent?: number;
  tableSelectionInfo?: TableSelectionInfo;
};

function $findAncestorTableCellInfo(
  selection: ReturnType<typeof $getSelection>,
): TableSelectionInfo | null {
  if (!$isRangeSelection(selection)) return null;

  const anchorCell = selection.anchor.getNode().getParents().find($isTableCellNode);
  if (!anchorCell || !$isTableCellNode(anchorCell)) return null;

  const row = anchorCell.getParent();
  if (!row || !$isTableRowNode(row)) return null;

  const table = row.getParent();
  if (!table || !$isTableNode(table)) return null;

  const rows = table.getChildren().filter($isTableRowNode);
  const rowIdx = rows.indexOf(row);
  const colIdx = row.getChildren().filter($isTableCellNode).indexOf(anchorCell);

  if (rowIdx === -1 || colIdx === -1) return null;

  return {
    tableNodeKey: table.getKey(),
    startRow: rowIdx,
    endRow: rowIdx,
    startCol: colIdx,
    endCol: colIdx,
  };
}

export const SIESTA_SELECTION_REQUEST_COMMAND: LexicalCommand<SiestaRequestPayload> = createCommand(
  'SIESTA_SELECTION_REQUEST',
);

export const SIESTA_CANCEL_INLINE_EDIT_COMMAND: LexicalCommand<void> = createCommand(
  'SIESTA_CANCEL_INLINE_EDIT',
);

type Props = {
  documentId?: string;
  documentOwner?: DocumentEditOwner | null;
  conversationId?: string;
  onProcessingComplete?: () => void;
  onInlineEditStart?: (top: number) => void;
  onInlineEditComplete?: () => void;
};

export function SiestaInlinePlugin({
  documentId,
  documentOwner,
  conversationId,
  onProcessingComplete,
  onInlineEditStart,
  onInlineEditComplete,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const { inlineEdit } = useInlineDocumentEdit(documentId);
  const { recordAction } = useRecordEditAction();
  const transformers = useMemo(() => createTransformers(editor), [editor]);
  const onProcessingCompleteRef = useRef(onProcessingComplete);
  const onInlineEditStartRef = useRef(onInlineEditStart);
  const onInlineEditCompleteRef = useRef(onInlineEditComplete);
  useEffect(() => {
    onProcessingCompleteRef.current = onProcessingComplete;
    onInlineEditStartRef.current = onInlineEditStart;
    onInlineEditCompleteRef.current = onInlineEditComplete;
  }, [onProcessingComplete, onInlineEditStart, onInlineEditComplete]);

  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  function finalizeRequest() {
    setPendingRequest(null);
    onProcessingCompleteRef.current?.();
    onInlineEditCompleteRef.current?.();
  }

  useEffect(() => {
    if (!pendingRequest || !documentId || !documentOwner) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const { markdown, promptText, tableSelectionInfo } = pendingRequest;

    inlineEdit({
      documentContent: markdown,
      editRequest: promptText,
      ...documentOwner,
      signal: controller.signal,
    })
      .then(replacementMarkdown => {
        if (controller.signal.aborted) return;

        const hasDeletion = replacementMarkdown.includes('source="deletion"');
        const hasInline = replacementMarkdown.includes('source="inline"');
        const isPureDeletion = hasDeletion && !hasInline;

        const isPartial =
          tableSelectionInfo !== undefined &&
          isPureDeletion &&
          editor.getEditorState().read(() => $isPartialColumnSelection(tableSelectionInfo));

        if (tableSelectionInfo && isPureDeletion && isPartial) {
          editor.update(
            () => {
              const tableNode = $getNodeByKey(tableSelectionInfo.tableNodeKey);
              if (!tableNode || !$isTableNode(tableNode)) return;

              const rows = tableNode.getChildren().filter($isTableRowNode);
              for (
                let rowIdx = tableSelectionInfo.startRow;
                rowIdx <= tableSelectionInfo.endRow;
                rowIdx++
              ) {
                const row = rows[rowIdx];
                if (!row) continue;
                const cells = row.getChildren().filter($isTableCellNode);
                for (
                  let colIdx = tableSelectionInfo.startCol;
                  colIdx <= tableSelectionInfo.endCol;
                  colIdx++
                ) {
                  const cell = cells[colIdx];
                  if (!cell) continue;
                  const suggestion = $createSuggestionNode('deletion', true);
                  const children = cell.getChildren();
                  suggestion.append(...children);
                  cell.append(suggestion);
                }
              }
            },
            { tag: 'siesta-replacement' },
          );

          finalizeRequest();
          return;
        }

        const restoreScroll = captureScrollPosition(editor);

        editor.update(
          () => {
            $addUpdateTag(SKIP_SCROLL_INTO_VIEW_TAG);
            $setEditorValue(replacementMarkdown, transformers);
          },
          { tag: 'siesta-replacement' },
        );

        restoreScroll();

        finalizeRequest();
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        notifyAndLogError('Siesta AI error', 'Failed to transform text. Please try again.', error, {
          prompt: promptText,
          markdown,
        });
        finalizeRequest();
      });
  }, [pendingRequest, editor, transformers, documentId, documentOwner, inlineEdit]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SIESTA_CANCEL_INLINE_EDIT_COMMAND,
        () => {
          abortControllerRef.current?.abort();
          finalizeRequest();
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerCommand(
        SIESTA_SELECTION_REQUEST_COMMAND,
        (payload: SiestaRequestPayload) => {
          const { promptText, toolbarTopPercent, tableSelectionInfo } = payload;

          if (documentId && conversationId) {
            recordAction(
              buildEditActionTelemetry({
                documentId,
                actionText: promptText,
                source: 'MANUAL',
                conversationId,
                owner: documentOwner,
              }),
            );
          }

          const restoreScroll = captureScrollPosition(editor);

          editor.update(() => {
            function $resolveTableSelectionMarkdown(info: TableSelectionInfo): string | null {
              const tableNode = $getNodeByKey(info.tableNodeKey);
              const isTable = tableNode !== null && $isTableNode(tableNode);
              const totalCols = isTable ? tableNode.getColumnCount() : 1;
              const totalRows = isTable
                ? tableNode.getChildren().filter($isTableRowNode).length
                : 1;
              const spansAllColumns = info.startCol === 0 && info.endCol >= totalCols - 1;
              const spansAllRows = info.startRow === 0 && info.endRow >= totalRows - 1;
              const isSingleCell = info.startRow === info.endRow && info.startCol === info.endCol;

              if (spansAllColumns && spansAllRows) {
                return $getTableSelectionMarkdown(
                  info.tableNodeKey,
                  info.startRow,
                  info.endRow,
                  transformers,
                );
              }
              if (isSingleCell && !spansAllColumns) {
                return $getTableCellMarkdown(
                  info.tableNodeKey,
                  info.startRow,
                  info.startCol,
                  transformers,
                );
              }
              return $getTableCellsMarkdown(info, transformers);
            }

            function dispatchTableSelection(info: TableSelectionInfo) {
              const markdown = $resolveTableSelectionMarkdown(info);

              if (!markdown?.trim()) {
                finalizeRequest();
                return;
              }

              $setSelection(null);
              onInlineEditStartRef.current?.(toolbarTopPercent ?? 50);
              setPendingRequest({ markdown, promptText, tableSelectionInfo: info });
            }

            if (tableSelectionInfo) {
              const tableNode = $getNodeByKey(tableSelectionInfo.tableNodeKey);
              if (tableNode && $isTableNode(tableNode)) {
                const tableElement = getTableElement(
                  tableNode,
                  editor.getElementByKey(tableNode.getKey()),
                );
                if (tableElement) {
                  const tableObserver = getTableObserverFromTableElement(tableElement);
                  tableObserver?.$clearHighlight();
                }
              }

              dispatchTableSelection(tableSelectionInfo);
              return;
            }

            const selection = $getSelection();

            if (!$isRangeSelection(selection) || selection.isCollapsed()) {
              return;
            }

            // Range selections inside table cells are redirected to the table
            // selection path to avoid broken replacement markers.
            const derivedTableInfo = $findAncestorTableCellInfo(selection);
            if (derivedTableInfo) {
              dispatchTableSelection(derivedTableInfo);
              return;
            }

            const markers = $insertReplacementMarkers();
            if (!markers) return;

            const root = $getRoot();
            const markdown = hoistReplacementTags($getEditorValue(transformers, root));

            $removeReplacementMarkers(markers);

            if (!markdown.trim()) {
              return;
            }

            $setSelection(null);

            onInlineEditStartRef.current?.(toolbarTopPercent ?? 50);
            setPendingRequest({ markdown, promptText });
          });

          restoreScroll();

          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
    );
  }, [editor, transformers, documentId, conversationId, documentOwner, recordAction]);

  return null;
}
